import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from './Modal';
import { Button } from './Button';
import QRScannerModule from 'shared/components/QRScanner';
const QRScanner = QRScannerModule as React.ComponentType<{ onScanSuccess: (data: string) => void; onScanError?: (err: string) => void; labels?: Record<string, string>; modes?: ('camera' | 'upload' | 'manual')[] }>;
import { useTranslation } from 'shared/i18n';
import { CheckCircle, XCircle, CreditCard, FileText, Lock } from 'lucide-react';
import { useAuthStore } from 'shared/store/authStore';
import { usePaymentModalStore } from '../../store/paymentModalStore';
import { unifiedAuth } from '../../api/authClient';
import UnifiedLoginForm from '../auth/UnifiedLoginForm';
import UnifiedRegisterForm from '../auth/UnifiedRegisterForm';

interface Company {
  id: string;
  name: string;
  tin: string;
  business_unit?: string;
  is_verified: boolean;
}

interface Receipt {
  iic: string;
  fiscal_number: string;
  tin: string;
  bus_unit?: string;
  tcr_code?: string;
  date_time: string;
  amount: number;
  tax_amount?: number;
  currency: string;
  payment_method?: string;
  items?: any[];
}

interface ParsedQRData {
  success: boolean;
  qr_data?: string;
  fiscal_number?: string;
  jib?: string;
  amount?: number;
  company?: Company;
  receipt?: Receipt;
  error?: string;
  error_code?: string;
  details?: string;
  /** Set when data came from verification URL parsing (fiscal or internal receipt); do not show raw URL. */
  fromVerification?: boolean;
  /** Internal receipt UUID (from parse-verification-url); passed to initiate-from-verification. */
  internalReceiptId?: string;
  /** Company (JIB) not registered on platform; show message and block proceed. */
  jibNotRegistered?: boolean;
  /** Fee mode: company_absorbs (default) or customer_pays */
  feeMode?: string;
  /** When fee_mode=customer_pays, the total amount the customer will be charged (receipt + fees) */
  customerTotal?: number;
}

type PaymentStep = 'scan' | 'card' | 'overview' | 'confirmation';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_BASE_URL = (() => {
  const env = (import.meta.env.VITE_API_URL ?? '').trim();
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api/v1';
  }
  return env ? `${env}/api/v1` : '/api/v1';
})();

/** Check if receipt (tin + pfr_number) is already paid; used to show error on step 1 instead of at payment. */
async function checkReceiptAlreadyPaid(
  apiBaseUrl: string,
  tin: string,
  pfrNumber: string
): Promise<{ already_paid: boolean; payment_id?: string }> {
  const tinTrim = (tin ?? '').trim();
  const pfrTrim = (pfrNumber ?? '').trim();
  if (!tinTrim || !pfrTrim) return { already_paid: false };
  const res = await fetch(`${apiBaseUrl}/payments/check-receipt`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tin: tinTrim, pfr_number: pfrTrim }),
  });
  const parsed = await parseJsonResponse(res);
  if ('errorCode' in parsed) return { already_paid: false };
  const data = parsed.data as { already_paid?: boolean; payment_id?: string };
  return {
    already_paid: !!data.already_paid,
    payment_id: data.payment_id ?? undefined,
  };
}

/** Error codes returned when response is not valid JSON (for i18n in UI). */
export type ParseJsonErrorCode = 'image_too_large' | 'server_error_page' | 'invalid_response';

/** Parse response body as JSON; if server returns HTML (e.g. SPA or error page), return an error code for translation. */
async function parseJsonResponse(
  response: Response
): Promise<{ data: Record<string, unknown> } | { errorCode: ParseJsonErrorCode }> {
  const text = await response.text();
  const ct = response.headers.get('content-type') ?? '';
  if (!ct.includes('application/json') || text.trimStart().startsWith('<')) {
    const status = response.status;
    const errorCode: ParseJsonErrorCode = status === 413 ? 'image_too_large' : 'server_error_page';
    return { errorCode };
  }
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    return { data };
  } catch {
    return { errorCode: 'invalid_response' };
  }
}

/** Returns true when the URL should be sent to parse-verification-url (any http(s) URL). */
function isHttpUrl(s: string): boolean {
  const t = (s || '').trim();
  return t.startsWith('http://') || t.startsWith('https://');
}

/**
 * Shared helper: call parse-verification-url, resolve company, build ParsedQRData.
 * Works for fiscal (suf.poreskaupravars.org) and internal (PROJECT_URL/payment/:id) URLs equally.
 */
async function parseVerificationAndBuildData(
  apiBaseUrl: string,
  url: string,
  t: { payment: Record<string, any> },
): Promise<{ data: ParsedQRData | null; error?: string }> {
  const verResp = await fetch(`${apiBaseUrl}/qr/parse-verification-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: url.trim() }),
  });
  const verParsed = await parseJsonResponse(verResp);
  if ('errorCode' in verParsed) {
    return { data: null, error: t.payment.errors[verParsed.errorCode] };
  }
  const verData = verParsed.data as {
    tin?: string; total_amount?: string; pfr_time?: string; pfr_number?: string;
    internal_receipt_id?: string; company_name?: string;
    fee_mode?: string; customer_total?: string;
  };
  if (!verResp.ok) {
    const detail = (verData as { detail?: { message?: string }; message?: string }).detail ?? verData;
    const msg = (typeof detail === 'object' && detail && (detail as { message?: string }).message)
      ? (detail as { message: string }).message
      : t.payment.errors.verification_link_failed;
    return { data: null, error: msg };
  }
  const tin = verData.tin;
  const totalAmount = verData.total_amount != null ? parseFloat(String(verData.total_amount)) : undefined;
  const pfrTime = verData.pfr_time ?? '';
  const pfrNumber = verData.pfr_number ?? '';
  const internalReceiptId = verData.internal_receipt_id ?? undefined;
  const feeMode = verData.fee_mode ?? undefined;
  const customerTotalRaw = verData.customer_total != null ? parseFloat(String(verData.customer_total)) : undefined;

  // If no usable data at all
  if ((tin == null || tin === '') && (totalAmount == null || !Number.isFinite(totalAmount))) {
    return { data: null, error: t.payment.scan?.paste_url_invalid || t.payment.errors.verification_link_failed };
  }

  // Check if already paid
  const check = await checkReceiptAlreadyPaid(apiBaseUrl, tin ?? '', pfrNumber);
  if (check.already_paid) {
    return {
      data: null,
      error: t.payment.errors.receipt_already_paid + (check.payment_id ? ` (${check.payment_id})` : ''),
    };
  }

  // Look up company by TIN
  let company: Company | null = null;
  let jibNotRegistered = false;
  let resolvedFeeMode = feeMode;
  if (tin) {
    const companyResp = await fetch(`${apiBaseUrl}/qr/company/${encodeURIComponent(tin)}`);
    if (companyResp.ok) {
      const companyJson = await companyResp.json() as Company & { fee_mode?: string };
      company = companyJson;
      // Use fee_mode from company lookup as source of truth (if not already set from verification response)
      if (!resolvedFeeMode && companyJson.fee_mode) {
        resolvedFeeMode = companyJson.fee_mode;
      }
    } else {
      jibNotRegistered = true;
    }
  }
  // For internal receipts without TIN in company lookup, use the company_name from parser
  if (!company && internalReceiptId && verData.company_name) {
    company = { id: '', name: verData.company_name, tin: tin ?? '', is_verified: true };
    jibNotRegistered = false; // Internal receipts are always from a registered company
  }

  const receipt: Receipt = {
    iic: pfrNumber,
    fiscal_number: pfrNumber,
    tin: tin ?? '',
    date_time: pfrTime,
    amount: totalAmount ?? 0,
    currency: 'BAM',
  };
  return {
    data: {
      success: true,
      fromVerification: true,
      internalReceiptId,
      jibNotRegistered,
      company: company ?? undefined,
      receipt,
      feeMode: resolvedFeeMode,
      customerTotal: customerTotalRaw && Number.isFinite(customerTotalRaw) ? customerTotalRaw : undefined,
    } as ParsedQRData,
  };
}

export const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose }) => {
  const { t: tFn } = useTranslation('portal');
  const t = { payment: tFn('payment', { returnObjects: true }) as Record<string, any> };
  const [currentStep, setCurrentStep] = useState<PaymentStep>('scan');
  const [parsedData, setParsedData] = useState<ParsedQRData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveCard, setSaveCard] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  /** When guest checks "save card", we require password; we create account and log in before payment. */
  const [guestPassword, setGuestPassword] = useState('');
  const [guestPasswordConfirm, setGuestPasswordConfirm] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  /** When payment fails on confirmation step: DUPLICATE_RECEIPT = already paid, PAYMENT_IN_PROGRESS = in progress */
  const [confirmationErrorCode, setConfirmationErrorCode] = useState<null | 'DUPLICATE_RECEIPT' | 'PAYMENT_IN_PROGRESS'>(null);
  const [scanOption, setScanOption] = useState<'camera' | 'upload' | 'url'>('camera');
  const [pastedUrl, setPastedUrl] = useState('');
  const uploadPhotoInputRef = useRef<HTMLInputElement>(null);
  /** On card step: show login/register inline instead of guest form. */
  const [cardStepSubView, setCardStepSubView] = useState<null | 'login' | 'register'>(null);
  /** Terms acceptance checkbox state */
  const [termsAccepted, setTermsAccepted] = useState(false);
  /** Saved cards for logged-in user */
  const [savedCards, setSavedCards] = useState<{ id: string; last4: string; card_brand: string; expiration_date: string }[]>([]);
  /** Selected saved card ID — null means "new card" */
  const [selectedSavedCardId, setSelectedSavedCardId] = useState<string | null>(null);
  /** Customer billing addresses */
  const [addresses, setAddresses] = useState<{ id: string; label?: string; full_name: string; address: string; city: string; zip: string; country: string; phone: string; is_default: boolean }[]>([]);
  /** Selected address ID for payment — null means "new address" */
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  /** Inline address form for new address or guest */
  const [addressForm, setAddressForm] = useState({ full_name: '', address: '', city: '', zip: '', country: 'BA', phone: '' });
  /** Whether to show the new address form */
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  /** Post-checkout registration: store pan_token data from last payment */
  const [lastPanTokenData, setLastPanTokenData] = useState<{ pan_token: string; last4: string; card_brand: string; expiration_date: string } | null>(null);
  const [postCheckoutPassword, setPostCheckoutPassword] = useState('');
  const [postCheckoutPasswordConfirm, setPostCheckoutPasswordConfirm] = useState('');
  const [postCheckoutRegistering, setPostCheckoutRegistering] = useState(false);
  const [postCheckoutRegistered, setPostCheckoutRegistered] = useState(false);
  const [postCheckoutError, setPostCheckoutError] = useState<string | null>(null);
  const [postCheckoutOtpPending, setPostCheckoutOtpPending] = useState(false);
  const [postCheckoutOtpCode, setPostCheckoutOtpCode] = useState('');
  const [postCheckoutResendCooldown, setPostCheckoutResendCooldown] = useState(0);
  /** Payment result data from API for confirmation display */
  const [paymentResult, setPaymentResult] = useState<{
    payment_id?: string;
    receipt_id?: string;
    monri_transaction_id?: string;
    amount?: string;
    status?: string;
    created_at?: string;
  } | null>(null);
  /** Monri Components integration state */
  const [monriSession, setMonriSession] = useState<{
    client_secret: string;
    authenticity_token: string;
    base_url: string;
    payment_id: string;
    receipt_id: string;
    amount: string;
  } | null>(null);
  const monriCardRef = useRef<HTMLDivElement>(null);
  const monriInstanceRef = useRef<any>(null);
  const monriCardComponentRef = useRef<any>(null);

  const customer = useAuthStore((s) => s.customer);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const setCustomerAuth = useAuthStore((s) => s.setCustomerAuth);
  const preloadUrlFromStore = usePaymentModalStore((s) => s.preloadUrl);
  const clearPreloadUrl = usePaymentModalStore((s) => s.clearPreloadUrl);
  /** Every user is also a customer; any logged-in user sees saved cards / new card (no guest, no email field). */
  const isLoggedInAsCustomer = !!(hasHydrated && customer && customer.user);
  /** Same as isLoggedInAsCustomer; kept for clarity where we hide sign in/register. */
  const isLoggedIn = isLoggedInAsCustomer;

  useEffect(() => {
    if (currentStep !== 'card') setCardStepSubView(null);
  }, [currentStep]);

  // When modal opens from /payment/:id, preload by calling parse-verification-url with the URL
  useEffect(() => {
    if (!isOpen || !preloadUrlFromStore || currentStep !== 'scan') return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    parseVerificationAndBuildData(API_BASE_URL, preloadUrlFromStore, t).then(({ data, error: errMsg }) => {
      if (cancelled) return;
      clearPreloadUrl();
      setLoading(false);
      if (errMsg || !data) {
        setError(errMsg || t.payment.errors.verification_link_failed);
        return;
      }
      if (data.success && data.receipt && data.company) {
        setParsedData(data);
        setCurrentStep('overview');
      } else {
        setError(t.payment.errors.verification_link_failed);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, preloadUrlFromStore]);

  useEffect(() => {
    if (!saveCard) {
      setGuestPassword('');
      setGuestPasswordConfirm('');
    }
  }, [saveCard]);

  useEffect(() => {
    if (currentStep === 'card' && customer?.user?.email) {
      setCustomerEmail(customer.user.email);
    }
  }, [currentStep, customer?.user?.email]);

  // Fetch saved cards + auto-create Monri session when entering card step (for logged-in users)
  const autoSessionTriggered = useRef(false);
  const savedCardsFetched = useRef(false);
  useEffect(() => {
    if (currentStep === 'card' && isLoggedInAsCustomer && !savedCardsFetched.current) {
      savedCardsFetched.current = true;
      const token = customer?.token;
      if (token) {
        const headers = { Authorization: `Bearer ${token}` };
        // Fetch saved cards and addresses in parallel
        Promise.all([
          fetch(`${API_BASE_URL}/payments/saved-cards`, { headers }).then((r) => (r.ok ? r.json() : [])),
          fetch(`${API_BASE_URL}/payments/addresses`, { headers }).then((r) => (r.ok ? r.json() : [])),
        ])
          .then(([cards, addrs]) => {
            const cardList = Array.isArray(cards) ? cards : [];
            const addrList = Array.isArray(addrs) ? addrs : [];
            setSavedCards(cardList);
            setAddresses(addrList);
            // Auto-select saved card: default card, or first if only one
            if (cardList.length === 1) {
              setSelectedSavedCardId(cardList[0].id);
            } else if (cardList.length > 1) {
              const defaultCard = cardList.find((c: any) => c.is_default);
              if (defaultCard) setSelectedSavedCardId(defaultCard.id);
            }
            // Auto-select default address
            const defaultAddr = addrList.find((a: any) => a.is_default);
            if (defaultAddr) setSelectedAddressId(defaultAddr.id);
            else if (addrList.length > 0) setSelectedAddressId(addrList[0].id);
            else setShowNewAddressForm(true);
            // If user has no saved cards, auto-create Monri session for new card
            if (cardList.length === 0 && !monriSession && !autoSessionTriggered.current && !loading) {
              autoSessionTriggered.current = true;
              handlePayment();
            }
          })
          .catch(() => {
            setSavedCards([]);
            setAddresses([]);
            setShowNewAddressForm(true);
          });
      }
    }
    // Guest: auto-create Monri session immediately
    if (currentStep === 'card' && !isLoggedInAsCustomer && !monriSession && !autoSessionTriggered.current && !loading) {
      autoSessionTriggered.current = true;
      handlePayment();
    }
    if (currentStep !== 'card') {
      autoSessionTriggered.current = false;
      savedCardsFetched.current = false;
      setSelectedSavedCardId(null);
      setShowNewAddressForm(false);
    }
  }, [currentStep, isLoggedInAsCustomer]);

  const steps: PaymentStep[] = ['scan', 'overview', 'card', 'confirmation'];
  const currentStepIndex = steps.indexOf(currentStep);

  const handleUploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_BASE_URL}/qr/parse-image`, {
        method: 'POST',
        body: form,
      });
      const parsed = await parseJsonResponse(response);
      if ('errorCode' in parsed) {
        setError(t.payment.errors[parsed.errorCode]);
        return;
      }
      const data = parsed.data;
      if (!response.ok) {
        const detail = data.detail || data;
        const msg = typeof detail === 'object' ? ((detail as { message?: string; error?: string }).message || (detail as { message?: string; error?: string }).error || 'Failed to process QR code') : String(detail);
        setError(msg);
        return;
      }
      const qrData = (data as { qr_data?: string }).qr_data;
      // Any URL (fiscal or internal): send to parse-verification-url
      if (qrData && isHttpUrl(qrData)) {
        const { data: verData, error: verError } = await parseVerificationAndBuildData(API_BASE_URL, qrData, t);
        if (verError || !verData) {
          setError(verError || t.payment.errors.verification_link_failed);
          return;
        }
        if (verData.success && verData.receipt && verData.company) {
          setParsedData(verData);
          setCurrentStep('overview');
          return;
        }
        setError(t.payment.errors.verification_link_failed);
        return;
      }
      setParsedData(data as unknown as ParsedQRData);
      setCurrentStep('overview');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      setError(tFn('payment.errors.qr_processing_error', { message: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const handleQRScan = async (scannedData: string) => {
    setError(null);
    setLoading(true);

    try {
      // Any URL (fiscal or internal): send to parse-verification-url
      if (isHttpUrl(scannedData)) {
        const { data, error: errMsg } = await parseVerificationAndBuildData(API_BASE_URL, scannedData, t);
        if (errMsg || !data) {
          setError(errMsg || t.payment.errors.verification_link_failed);
          return;
        }
        if (data.success && data.receipt && data.company) {
          setParsedData(data);
          setCurrentStep('overview');
          return;
        }
        setError(t.payment.errors.verification_link_failed);
        return;
      }

      // Non-URL QR data: parse as fiscal QR
      const response = await fetch(`${API_BASE_URL}/qr/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ qr_data: scannedData }),
      });

      const parsed = await parseJsonResponse(response);
      if ('errorCode' in parsed) {
        setError(t.payment.errors[parsed.errorCode]);
        return;
      }
      const data = parsed.data;

      if (!response.ok) {
        const detail = data.detail || data;
        const msg = typeof detail === 'object' ? ((detail as { message?: string; error?: string }).message || (detail as { message?: string; error?: string }).error || 'Failed to process QR code') : String(detail);
        setError(msg);
        return;
      }

      const parsedAsQR = data as unknown as ParsedQRData;
      const receiptFromQR = parsedAsQR?.receipt;
      if (receiptFromQR?.tin && receiptFromQR?.fiscal_number) {
        const check = await checkReceiptAlreadyPaid(API_BASE_URL, receiptFromQR.tin, receiptFromQR.fiscal_number);
        if (check.already_paid) {
          setError(
            t.payment.errors.receipt_already_paid +
              (check.payment_id ? ` (${check.payment_id})` : '')
          );
          return;
        }
      }

      setParsedData(parsedAsQR);
      setCurrentStep('overview');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      setError(tFn('payment.errors.qr_processing_error_with_data', { message: errorMsg }));
    } finally {
      setLoading(false);
    }
  };

  const handlePasteUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = pastedUrl.trim();
    setError(null);
    if (!url) {
      setError(t.payment.scan.enter_qr_data);
      return;
    }
    if (!isHttpUrl(url)) {
      setError(t.payment.scan.paste_url_hint + ' (Enter a valid link starting with https://)');
      return;
    }
    // Send any URL to parse-verification-url
    setLoading(true);
    try {
      const { data, error: errMsg } = await parseVerificationAndBuildData(API_BASE_URL, url, t);
      if (errMsg || !data) {
        setError(errMsg || t.payment.errors.verification_link_failed);
        setPastedUrl('');
        return;
      }
      if (data.success && data.receipt && data.company) {
        setParsedData(data);
        setPastedUrl('');
        setCurrentStep('overview');
        return;
      }
      setError(t.payment.errors.verification_link_failed);
      setPastedUrl('');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Network error';
      setError(errorMsg);
      setPastedUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const handlePreviousStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    setConfirmationErrorCode(null);

    const emailToSend = (customer?.user?.email) || customerEmail || undefined;

    // Guest chose "save card": require password, create account and log in before payment
    if (!isLoggedIn && saveCard) {
      const pwd = (guestPassword ?? '').trim();
      const pwdConfirm = (guestPasswordConfirm ?? '').trim();
      if (pwd.length < 8) {
        setError(t.payment.errors.password_min);
        setLoading(false);
        return;
      }
      if (pwd !== pwdConfirm) {
        setError(t.payment.errors.password_mismatch);
        setLoading(false);
        return;
      }
      const emailTrim = (customerEmail ?? '').trim();
      if (!emailTrim) {
        setError(t.payment.errors.email_required);
        setLoading(false);
        return;
      }
      try {
        await unifiedAuth.registerCustomer(emailTrim, pwd);
      } catch (e: unknown) {
        const detail =
          e &&
          typeof e === 'object' &&
          'response' in e &&
          (e.response as { data?: { detail?: string } })?.data?.detail;
        setError(detail ? String(detail) : t.payment.errors.register_failed);
        setLoading(false);
        return;
      }
      try {
        const { data: loginRes } = await unifiedAuth.login(emailTrim, pwd);
        if (
          loginRes.access_token &&
          loginRes.user_id != null &&
          loginRes.email != null &&
          loginRes.role != null
        ) {
          setCustomerAuth(loginRes.access_token, {
            id: loginRes.user_id,
            email: loginRes.email,
            role: loginRes.role,
          });
        }
      } catch {
        setError(t.payment.errors.register_failed);
        setLoading(false);
        return;
      }
      // Now user is logged in; continue with payment below (emailToSend is still customerEmail)
    }

    try {
      // Re-check on step 4: maybe someone else paid in the meantime
      if (parsedData?.receipt?.tin && parsedData?.receipt?.fiscal_number) {
        const preCheck = await checkReceiptAlreadyPaid(
          API_BASE_URL,
          parsedData.receipt.tin,
          parsedData.receipt.fiscal_number,
        );
        if (preCheck.already_paid) {
          setError(
            t.payment.errors.receipt_already_paid +
              (preCheck.payment_id ? ` (${preCheck.payment_id})` : ''),
          );
          setConfirmationErrorCode('DUPLICATE_RECEIPT');
          setPaymentSuccess(false);
          setCurrentStep('confirmation');
          setLoading(false);
          return;
        }
      }

      // All verification-based payments (fiscal and internal receipts) use the same endpoint
      if (parsedData?.fromVerification && parsedData?.receipt) {
        const payload: Record<string, unknown> = {
          tin: parsedData.receipt.tin,
          total_amount: parsedData.receipt.amount,
          pfr_number: parsedData.receipt.fiscal_number,
          pfr_time: parsedData.receipt.date_time ?? '',
          customer_email: emailToSend,
          customer_phone: undefined,
        };
        // Pass internal_receipt_id so backend can link the payment to the internal receipt
        if (parsedData.internalReceiptId) {
          payload.internal_receipt_id = parsedData.internalReceiptId;
        }
        const res = await fetch(`${API_BASE_URL}/payments/initiate-from-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const text = await res.text();
        const ct = res.headers.get('content-type') ?? '';
        const isJson = ct.includes('application/json') && text.trimStart().startsWith('{');
        const data = isJson ? (JSON.parse(text) as Record<string, unknown>) : {};
        if (!res.ok) {
          const detail = (data.detail ?? data) as { message?: string; error?: string };
          const errMsg =
            typeof detail === 'object' && detail?.error === 'DUPLICATE_RECEIPT'
              ? t.payment.errors.receipt_already_paid
              : typeof detail === 'object' && detail?.error === 'ALREADY_PAID'
                ? t.payment.errors.receipt_already_paid
                : typeof detail === 'object' && detail?.error === 'PAYMENT_IN_PROGRESS'
                  ? t.payment.errors.payment_in_progress
                  : typeof detail === 'object' && detail?.error === 'RECEIPT_DELETED'
                    ? t.payment.errors.receipt_deleted
                    : typeof detail === 'object' && detail?.message
                      ? detail.message
                      : t.payment.errors.payment_failed;
          if (typeof detail === 'object' && (detail?.error === 'DUPLICATE_RECEIPT' || detail?.error === 'ALREADY_PAID')) {
            setConfirmationErrorCode('DUPLICATE_RECEIPT');
          } else if (typeof detail === 'object' && detail?.error === 'PAYMENT_IN_PROGRESS') {
            setConfirmationErrorCode('PAYMENT_IN_PROGRESS');
          }
          setError(errMsg);
          setPaymentSuccess(false);
          setCurrentStep('confirmation');
          return;
        }
        // Check if Monri session was created (real payment gateway)
        if (data.monri_client_secret && data.monri_authenticity_token && data.monri_base_url) {
          setMonriSession({
            client_secret: data.monri_client_secret as string,
            authenticity_token: data.monri_authenticity_token as string,
            base_url: data.monri_base_url as string,
            payment_id: data.payment_id as string,
            receipt_id: data.receipt_id as string,
            amount: data.amount as string,
          });
          setLoading(false);
          return;
        }
        setPaymentResult({
          payment_id: data.payment_id as string | undefined,
          receipt_id: data.receipt_id as string | undefined,
          monri_transaction_id: data.monri_transaction_id as string | undefined,
          amount: data.amount as string | undefined,
          status: data.status as string | undefined,
          created_at: new Date().toISOString(),
        });
        setPaymentSuccess(true);
        setCurrentStep('confirmation');
        return;
      }
      if (parsedData?.qr_data) {
        const res = await fetch(`${API_BASE_URL}/payments/initiate-from-qr`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            qr_data: parsedData.qr_data,
            customer_email: emailToSend,
            customer_phone: undefined,
          }),
        });
        const text = await res.text();
        const ct = res.headers.get('content-type') ?? '';
        const isJson = ct.includes('application/json') && text.trimStart().startsWith('{');
        const data = isJson ? (JSON.parse(text) as Record<string, unknown>) : {};
        if (!res.ok) {
          const detail = (data.detail ?? data) as { message?: string; error?: string };
          const errMsg =
            typeof detail === 'object' && detail?.error === 'DUPLICATE_RECEIPT'
              ? t.payment.errors.receipt_already_paid
              : typeof detail === 'object' && detail?.error === 'PAYMENT_IN_PROGRESS'
                ? t.payment.errors.payment_in_progress
                : typeof detail === 'object' && detail?.message
                  ? detail.message
                  : t.payment.errors.payment_failed;
          if (typeof detail === 'object' && detail?.error === 'DUPLICATE_RECEIPT') {
            setConfirmationErrorCode('DUPLICATE_RECEIPT');
          } else if (typeof detail === 'object' && detail?.error === 'PAYMENT_IN_PROGRESS') {
            setConfirmationErrorCode('PAYMENT_IN_PROGRESS');
          }
          setError(errMsg);
          setPaymentSuccess(false);
          setCurrentStep('confirmation');
          return;
        }
        // Check if Monri session was created (real payment gateway)
        if (data.monri_client_secret && data.monri_authenticity_token && data.monri_base_url) {
          setMonriSession({
            client_secret: data.monri_client_secret as string,
            authenticity_token: data.monri_authenticity_token as string,
            base_url: data.monri_base_url as string,
            payment_id: data.payment_id as string,
            receipt_id: data.receipt_id as string,
            amount: data.amount as string,
          });
          setLoading(false);
          return;
        }
        setPaymentResult({
          payment_id: data.payment_id as string | undefined,
          receipt_id: data.receipt_id as string | undefined,
          monri_transaction_id: data.monri_transaction_id as string | undefined,
          amount: data.amount as string | undefined,
          status: data.status as string | undefined,
          created_at: new Date().toISOString(),
        });
        setPaymentSuccess(true);
        setCurrentStep('confirmation');
        return;
      }
      setError(t.payment.errors.payment_failed);
      setPaymentSuccess(false);
      setCurrentStep('confirmation');
    } catch (err) {
      setError(t.payment.errors.payment_failed);
      setPaymentSuccess(false);
      setCurrentStep('confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentStep('scan');
    setParsedData(null);
    setError(null);
    setConfirmationErrorCode(null);
    setCustomerEmail('');
    setSaveCard(false);
    setGuestPassword('');
    setGuestPasswordConfirm('');
    setPaymentSuccess(false);
    setScanOption('camera');
    setPastedUrl('');
    setTermsAccepted(false);
    setPaymentResult(null);
    setMonriSession(null);
    onClose();
  };

  const renderStepIndicator = () => {
    const visualSteps = steps;
    const visualIndex = visualSteps.indexOf(currentStep);
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-0 mb-6">
        {visualSteps.map((step, index) => (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  index <= visualIndex
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {index + 1}
              </div>
              <span className="text-xs mt-1 text-gray-600">{t.payment.steps[step]}</span>
            </div>
            {index < visualSteps.length - 1 && (
              <div
                className={`w-16 h-1 mx-2 transition-all ${
                  index < visualIndex ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderScanStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-gray-900">{t.payment.scan.title}</h3>
        <p className="text-gray-600 mt-2">{t.payment.scan.description}</p>
      </div>

      <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-1">
        <button
          type="button"
          onClick={() => { setScanOption('camera'); setError(null); }}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            scanOption === 'camera' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t.payment.scan.option_camera}
        </button>
        <button
          type="button"
          onClick={() => { setScanOption('upload'); setError(null); }}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            scanOption === 'upload' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t.payment.scan.option_upload_photo}
        </button>
        <button
          type="button"
          onClick={() => { setScanOption('url'); setError(null); }}
          className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            scanOption === 'url' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t.payment.scan.option_paste_url}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {scanOption === 'camera' && (
        <QRScanner
          modes={['camera']}
          onScanSuccess={handleQRScan}
          onScanError={(err: string) => setError(err)}
          labels={{
            camera: t.payment.scan.camera,
            upload_image: t.payment.scan.upload_image,
            manual_entry: t.payment.scan.manual_entry,
            start_camera: t.payment.scan.start_camera,
            stop_scanning: t.payment.scan.stop_scanning,
            camera_hint: t.payment.scan.camera_hint,
            upload_hint: t.payment.scan.upload_hint,
            manual_placeholder: t.payment.scan.manual_placeholder,
            process_qr_data: t.payment.scan.process_qr_data,
            manual_hint_example: t.payment.scan.manual_hint_example,
            no_qr_in_image: t.payment.scan.no_qr_in_image,
            enter_qr_data: t.payment.scan.enter_qr_data,
            scan_current_frame: t.payment.scan.scan_current_frame,
            processing: t.payment.scan.processing,
          }}
        />
      )}

      {scanOption === 'upload' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-sm text-blue-800 mb-3">{t.payment.scan.upload_hint}</p>
          <input
            ref={uploadPhotoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleUploadPhoto}
            className="hidden"
            aria-label="Upload photo of receipt"
          />
          <button
            type="button"
            onClick={() => uploadPhotoInputRef.current?.click()}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed text-sm font-medium"
          >
            {loading ? t.payment.scan.processing : t.payment.scan.option_upload_photo}
          </button>
        </div>
      )}

      {scanOption === 'url' && (
        <form onSubmit={handlePasteUrl} className="space-y-3">
          <input
            type="url"
            value={pastedUrl}
            onChange={(e) => setPastedUrl(e.target.value)}
            placeholder={t.payment.scan.paste_url_placeholder}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading}
            aria-label="Verification link"
          />
          <p className="text-xs text-gray-500">{t.payment.scan.paste_url_hint}</p>
          <button
            type="submit"
            disabled={loading || !pastedUrl.trim()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed text-sm font-medium"
          >
            {loading ? t.payment.scan.processing : t.payment.scan.process_url}
          </button>
        </form>
      )}
    </div>
  );

  /** Get the resolved address data for Monri (from selected address or form). */
  const getResolvedAddress = (): { full_name: string; address: string; city: string; zip: string; country: string; phone: string } | null => {
    if (isLoggedInAsCustomer && selectedAddressId) {
      const addr = addresses.find((a) => a.id === selectedAddressId);
      if (addr) return addr;
    }
    // Use inline form
    if (addressForm.full_name.trim() && addressForm.address.trim()) {
      return addressForm;
    }
    return null;
  };

  /** Save a new address to backend and update state. */
  const saveNewAddress = async (): Promise<string | null> => {
    const token = customer?.token;
    if (!token) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/payments/addresses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(addressForm),
      });
      if (!res.ok) return null;
      const newAddr = await res.json();
      setAddresses((prev) => [...prev, newAddr]);
      setSelectedAddressId(newAddr.id);
      setShowNewAddressForm(false);
      return newAddr.id;
    } catch {
      return null;
    }
  };

  /** Render inline address form fields. */
  const renderAddressForm = () => (
    <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
      <p className="text-sm font-medium text-gray-700">{t.payment.address?.billing_address || 'Billing address'}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <input
            type="text"
            value={addressForm.full_name}
            onChange={(e) => setAddressForm((p) => ({ ...p, full_name: e.target.value }))}
            placeholder={t.payment.address?.full_name || 'Full name'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={100}
          />
        </div>
        <div className="sm:col-span-2">
          <input
            type="text"
            value={addressForm.address}
            onChange={(e) => setAddressForm((p) => ({ ...p, address: e.target.value }))}
            placeholder={t.payment.address?.address || 'Street address'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={255}
          />
        </div>
        <div>
          <input
            type="text"
            value={addressForm.city}
            onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
            placeholder={t.payment.address?.city || 'City'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={100}
          />
        </div>
        <div>
          <input
            type="text"
            value={addressForm.zip}
            onChange={(e) => setAddressForm((p) => ({ ...p, zip: e.target.value }))}
            placeholder={t.payment.address?.zip || 'ZIP / Postal code'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={20}
          />
        </div>
        <div>
          <select
            value={addressForm.country}
            onChange={(e) => setAddressForm((p) => ({ ...p, country: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="BA">{t.payment.address?.country_ba || 'Bosnia and Herzegovina'}</option>
            <option value="HR">{t.payment.address?.country_hr || 'Croatia'}</option>
            <option value="RS">{t.payment.address?.country_rs || 'Serbia'}</option>
            <option value="ME">{t.payment.address?.country_me || 'Montenegro'}</option>
            <option value="SI">{t.payment.address?.country_si || 'Slovenia'}</option>
            <option value="AT">{t.payment.address?.country_at || 'Austria'}</option>
            <option value="DE">{t.payment.address?.country_de || 'Germany'}</option>
          </select>
        </div>
        <div>
          <input
            type="tel"
            value={addressForm.phone}
            onChange={(e) => setAddressForm((p) => ({ ...p, phone: e.target.value }))}
            placeholder={t.payment.address?.phone || 'Phone'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={50}
          />
        </div>
      </div>
    </div>
  );

  /** Render address selector (existing addresses + new address option). */
  const renderAddressSelector = () => {
    if (addresses.length === 0) {
      return renderAddressForm();
    }
    return (
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">{t.payment.address?.billing_address || 'Billing address'}</p>
        {addresses.map((addr) => (
          <button
            key={addr.id}
            type="button"
            onClick={() => { setSelectedAddressId(addr.id); setShowNewAddressForm(false); }}
            className={`w-full flex items-center justify-between p-3 rounded-lg border transition text-left ${
              selectedAddressId === addr.id && !showNewAddressForm
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="text-sm">
              <span className="font-medium text-gray-900">{addr.full_name}</span>
              <span className="text-gray-500 ml-2">{addr.address}, {addr.city} {addr.zip}</span>
              {addr.is_default && <span className="text-xs text-blue-600 ml-2">({t.payment.address?.default_label || 'default'})</span>}
            </div>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selectedAddressId === addr.id && !showNewAddressForm ? 'border-blue-500' : 'border-gray-300'
            }`}>
              {selectedAddressId === addr.id && !showNewAddressForm && <div className="w-2 h-2 rounded-full bg-blue-500" />}
            </div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => { setShowNewAddressForm(true); setSelectedAddressId(null); }}
          className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
            showNewAddressForm
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <span className="text-sm font-medium text-gray-900">{t.payment.address?.new_address || 'Use a different address'}</span>
          <span className="text-xs text-blue-600">+</span>
        </button>
        {showNewAddressForm && renderAddressForm()}
      </div>
    );
  };

  /** Check if address is valid (either selected or form filled). */
  const isAddressValid = (): boolean => {
    if (isLoggedInAsCustomer && selectedAddressId && !showNewAddressForm) return true;
    // Check inline form
    return !!(addressForm.full_name.trim() && addressForm.address.trim() && addressForm.city.trim() && addressForm.zip.trim() && addressForm.phone.trim());
  };

  const renderCardStep = () => {
    if (!hasHydrated) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <CreditCard className="w-12 h-12 mx-auto text-blue-600 mb-3" />
            <h3 className="text-xl font-semibold text-gray-900">{t.payment.card.title}</h3>
          </div>
          <div className="flex justify-center py-8">
            <p className="text-gray-500">{t.payment.scan.processing}</p>
          </div>
        </div>
      );
    }
    if (cardStepSubView === 'login') {
      return (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setCardStepSubView(null)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← {t.payment.card.back_to_payment}
          </button>
          <UnifiedLoginForm
            embedded
            onSuccess={() => setCardStepSubView(null)}
            onOpenRegister={() => setCardStepSubView('register')}
          />
        </div>
      );
    }
    if (cardStepSubView === 'register') {
      return (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setCardStepSubView(null)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            ← {t.payment.card.back_to_payment}
          </button>
          <UnifiedRegisterForm
            embedded
            defaultRegisterAsCompany={false}
            onOpenLogin={() => setCardStepSubView('login')}
            onSuccess={() => setCardStepSubView('login')}
          />
        </div>
      );
    }

    // Logged-in user with saved cards — show card selection before creating Monri session
    if (isLoggedInAsCustomer && savedCards.length > 0 && !monriSession) {
      const brandIcon = (brand: string) => {
        const b = (brand || '').toLowerCase();
        if (b.includes('visa')) return '💳 Visa';
        if (b.includes('master')) return '💳 Mastercard';
        if (b.includes('maestro')) return '💳 Maestro';
        return '💳 ' + (brand || 'Card');
      };
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <CreditCard className="w-12 h-12 mx-auto text-blue-600 mb-3" />
            <h3 className="text-xl font-semibold text-gray-900">{t.payment.card.title}</h3>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <span className="text-2xl font-bold text-blue-600">
              {parsedData?.customerTotal ?? parsedData?.receipt?.amount ?? ''} {parsedData?.receipt?.currency || 'BAM'}
            </span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
          )}

          <p className="text-sm font-medium text-gray-700">{t.payment.savedCards?.choose_card || 'Choose a payment method'}</p>

          <div className="space-y-2">
            {savedCards.map((card) => (
              <button
                key={card.id}
                type="button"
                onClick={() => setSelectedSavedCardId(card.id)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
                  selectedSavedCardId === card.id
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm">{brandIcon(card.card_brand)}</span>
                  <span className="text-sm font-medium text-gray-900">•••• {card.last4}</span>
                  {card.expiration_date && (
                    <span className="text-xs text-gray-500">{card.expiration_date}</span>
                  )}
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  selectedSavedCardId === card.id ? 'border-blue-500' : 'border-gray-300'
                }`}>
                  {selectedSavedCardId === card.id && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                </div>
              </button>
            ))}

            {/* New card option */}
            <button
              type="button"
              onClick={() => {
                setSelectedSavedCardId(null);
                if (!monriSession && !loading) {
                  handlePayment();
                }
              }}
              className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
                selectedSavedCardId === null && monriSession
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <CreditCard className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-900">{t.payment.savedCards?.new_card || 'Pay with a new card'}</span>
              </div>
              <span className="text-xs text-blue-600">+</span>
            </button>
          </div>

          {/* Billing address selector */}
          {renderAddressSelector()}

          {/* Terms acceptance checkbox */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 mr-3"
            />
            <label htmlFor="termsAccepted" className="text-sm text-gray-700">
              {t.payment.card.terms_checkbox_label
                .replace('<termsLink>', '')
                .replace('</termsLink>', '')
                .replace('<privacyLink>', '')
                .replace('</privacyLink>', '')
              }
              <span className="block mt-1">
                <Link to="/terms" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.termsOfService')}</Link>
                {' · '}
                <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.privacyPolicy')}</Link>
                {' · '}
                <Link to="/refund-policy" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.refundPolicy')}</Link>
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-center">
            <Button variant="outline" onClick={handlePreviousStep} fullWidth>
              {t.payment.buttons.back}
            </Button>
            {selectedSavedCardId && (
              <Button
                onClick={handlePayWithSavedCard}
                loading={loading}
                disabled={!termsAccepted || !isAddressValid()}
                fullWidth
              >
                {t.payment.monri?.confirm_payment || 'Pay'}
              </Button>
            )}
          </div>

          {/* Security + card brands */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">{t.payment.card.security_notice}</p>
                <p className="text-xs text-green-700 mt-1">{t.payment.card.security_notice_text}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Monri session created — show Monri iframe for new card payment
    if (monriSession) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <CreditCard className="w-12 h-12 mx-auto text-blue-600 mb-3" />
            <h3 className="text-xl font-semibold text-gray-900">{t.payment.card.title}</h3>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <span className="text-2xl font-bold text-blue-600">
              {monriSession.amount} {parsedData?.receipt?.currency || 'BAM'}
            </span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
          )}

          {/* Back to saved cards link (if user has saved cards) */}
          {savedCards.length > 0 && (
            <button
              type="button"
              onClick={() => { setMonriSession(null); setSelectedSavedCardId(null); }}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              ← {t.payment.savedCards?.back_to_cards || 'Back to saved cards'}
            </button>
          )}

          {/* Guest: email field */}
          {!isLoggedInAsCustomer && (
            <div>
              <label htmlFor="guestEmail" className="block text-sm font-medium text-gray-700 mb-1">
                {t.payment.card.email_label}
              </label>
              <input
                type="email"
                id="guestEmail"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t.payment.card.email_placeholder}
              />
            </div>
          )}

          {/* Monri Components card form — new card */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t.payment.monri?.card_details_title || 'Enter card details'}</p>
            <div
              id="monri-card-element"
              ref={monriCardRef}
              className="border border-gray-300 rounded-lg p-4 min-h-[120px] bg-white"
            />
            <div id="monri-card-errors" className="text-red-600 text-sm mt-1" role="alert" />
          </div>

          {/* Billing address */}
          {isLoggedInAsCustomer ? renderAddressSelector() : renderAddressForm()}

          {/* Guest: sign in / register option */}
          {!isLoggedIn && (
            <div className="border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600 mb-2">{t.payment.card.or_sign_in_register}</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setCardStepSubView('login')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t.payment.card.sign_in}
                </button>
                <button
                  type="button"
                  onClick={() => setCardStepSubView('register')}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t.payment.card.register}
                </button>
              </div>
            </div>
          )}

          {/* Terms acceptance checkbox */}
          <div className="flex items-start">
            <input
              type="checkbox"
              id="termsAccepted"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 mr-3"
            />
            <label htmlFor="termsAccepted" className="text-sm text-gray-700">
              {t.payment.card.terms_checkbox_label
                .replace('<termsLink>', '')
                .replace('</termsLink>', '')
                .replace('<privacyLink>', '')
                .replace('</privacyLink>', '')
              }
              <span className="block mt-1">
                <Link to="/terms" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.termsOfService')}</Link>
                {' · '}
                <Link to="/privacy" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.privacyPolicy')}</Link>
                {' · '}
                <Link to="/refund-policy" target="_blank" className="text-blue-600 hover:underline text-xs">{tFn('footer.refundPolicy')}</Link>
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-center">
            <Button variant="outline" onClick={handlePreviousStep} fullWidth>
              {t.payment.buttons.back}
            </Button>
            <Button
              onClick={handleMonriConfirmPayment}
              loading={loading}
              disabled={!termsAccepted || !isAddressValid() || (!isLoggedInAsCustomer && !customerEmail)}
              fullWidth
            >
              {t.payment.monri?.confirm_payment || 'Pay'}
            </Button>
          </div>

          {/* Security + card brands */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">{t.payment.card.security_notice}</p>
                <p className="text-xs text-green-700 mt-1">{t.payment.card.security_notice_text}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a href="https://www.mastercard.ba/bs-ba.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/mc-id-check.webp" alt="Mastercard ID Check" height="30" className="h-[30px] w-auto" />
            </a>
            <a href="https://www.visa.co.uk/about-visa/visa-in-europe.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/visa-secure.webp" alt="Visa Secure" height="30" className="h-[30px] w-auto" />
            </a>
            <img src="/images/payment/payweb.webp" alt="PayWeb" height="30" className="h-[30px] w-auto" />
            <a href="https://www.mastercard.ba/bs-ba.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/mc.webp" alt="Mastercard" height="30" className="h-[30px] w-auto" />
            </a>
            <a href="https://brand.mastercard.com/brandcenter/more-about-our-brands.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/maestro.webp" alt="Maestro" height="30" className="h-[30px] w-auto" />
            </a>
            <a href="https://www.visa.co.uk/about-visa/visa-in-europe.html" target="_blank" rel="noopener noreferrer">
              <img src="/images/payment/visa.webp" alt="Visa" height="30" className="h-[30px] w-auto" />
            </a>
          </div>
          <div className="text-center text-xs text-gray-400">
            <Lock className="w-3 h-3 inline mr-1" />
            {t.payment.monri?.pci_notice || 'Card data is processed directly by Monri payment gateway. We never see your full card number.'}
          </div>
        </div>
      );
    }

    // Monri session not yet created — loading (session is being auto-created for both guest and logged-in)
    return (
      <div className="space-y-6">
        <div className="text-center mb-4">
          <CreditCard className="w-12 h-12 mx-auto text-blue-600 mb-3" />
          <h3 className="text-xl font-semibold text-gray-900">{t.payment.card.title}</h3>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-center py-8">
          <p className="text-gray-500">{t.payment.monri?.form_loading || 'Loading payment form...'}</p>
        </div>
        <div className="flex justify-center">
          <Button variant="outline" onClick={handlePreviousStep}>
            {t.payment.buttons.back}
          </Button>
        </div>
      </div>
    );
  };

  const renderOverviewStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <FileText className="w-12 h-12 mx-auto text-blue-600 mb-3" />
        <h3 className="text-xl font-semibold text-gray-900">{t.payment.overview.title}</h3>
      </div>

      {parsedData && (
        <>
          {parsedData.jibNotRegistered && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
              <p className="text-sm font-medium">{t.payment.errors.jib_not_registered}</p>
              <p className="text-sm mt-1">{t.payment.errors.jib_not_registered_hint}</p>
            </div>
          )}
          {parsedData.qr_data && !parsedData.fromVerification && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-2">
              <div className="text-gray-600 text-sm font-medium">{t.payment.overview.scanned_qr_label}</div>
              <p className="font-mono text-sm break-all text-gray-800">{parsedData.qr_data}</p>
            </div>
          )}
          {(parsedData.company || parsedData.receipt) && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
              {/* Merchant information block */}
              {parsedData.company && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.payment.overview.merchant_info}</p>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.payment.overview.company}:</span>
                    <span className="font-semibold">{parsedData.company.name}</span>
                  </div>
                  {parsedData.company.tin && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.payment.overview.company_tin}:</span>
                      <span className="font-mono text-sm">{parsedData.company.tin}</span>
                    </div>
                  )}
                </div>
              )}
              {parsedData.receipt && (
                <>
                  <div className="border-t border-gray-200 pt-3 mt-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.payment.overview.invoice_number}:</span>
                      <span className="font-mono text-sm">{parsedData.receipt.fiscal_number}</span>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    {parsedData.feeMode === 'customer_pays' && parsedData.customerTotal != null && parsedData.customerTotal !== parsedData.receipt.amount ? (
                      <>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-600">{t.payment.overview.receipt_amount ?? t.payment.overview.amount}:</span>
                          <span className="text-lg text-gray-700">
                            {parsedData.receipt.amount?.toFixed(2)} {parsedData.receipt.currency || 'BAM'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600 font-medium">{t.payment.overview.you_pay}:</span>
                          <span className="text-2xl font-bold text-blue-600">
                            {parsedData.customerTotal?.toFixed(2)} {parsedData.receipt.currency || 'BAM'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">{t.payment.overview.customer_pays_hint}</p>
                      </>
                    ) : (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">{t.payment.overview.amount}:</span>
                        <span className="text-2xl font-bold text-blue-600">
                          {parsedData.receipt.amount?.toFixed(2)} {parsedData.receipt.currency || 'BAM'}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">{t.payment.overview.payment_method}:</span>
                <span>{t.payment.overview.payment_method_card}</span>
              </div>
            </div>
          )}

        </>
      )}

      <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-center">
        <Button variant="outline" onClick={handlePreviousStep} fullWidth>
          {t.payment.buttons.back}
        </Button>
        <Button
          onClick={handleNextStep}
          fullWidth
          disabled={parsedData?.jibNotRegistered === true}
        >
          {t.payment.buttons.next}
        </Button>
      </div>
    </div>
  );

  const renderConfirmationStep = () => (
    <div className="text-center space-y-6 py-8">
      {paymentSuccess ? (
        <>
          <CheckCircle className="w-20 h-20 mx-auto text-green-500" />
          <h3 className="text-2xl font-bold text-green-600">{t.payment.confirmation.success}</h3>
          <p className="text-gray-600">
            {t.payment.confirmation.payment_recorded_hint}
          </p>

          {parsedData && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <div className="space-y-2 text-sm">
                {/* Transaction IDs */}
                {paymentResult?.payment_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.payment.confirmation.payment_id}:</span>
                    <span className="font-mono text-xs">{paymentResult.payment_id}</span>
                  </div>
                )}
                {paymentResult?.monri_transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.payment.confirmation.transaction_id}:</span>
                    <span className="font-mono text-xs">{paymentResult.monri_transaction_id}</span>
                  </div>
                )}
                {/* Date/time */}
                {paymentResult?.created_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.payment.confirmation.payment_date}:</span>
                    <span className="text-xs">{new Date(paymentResult.created_at).toLocaleString()}</span>
                  </div>
                )}

                <div className="border-t border-gray-200 my-2" />

                {parsedData.receipt && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.payment.confirmation.amount_paid}:</span>
                      <span className="font-semibold">
                        {(parsedData.feeMode === 'customer_pays' && parsedData.customerTotal != null
                          ? parsedData.customerTotal.toFixed(2)
                          : parsedData.receipt.amount?.toFixed(2)
                        )} BAM
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.payment.overview.invoice_number}:</span>
                      <span className="font-mono text-xs">{parsedData.receipt.fiscal_number}</span>
                    </div>
                  </>
                )}

                <div className="border-t border-gray-200 my-2" />

                {/* Merchant details */}
                {parsedData.company && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t.payment.confirmation.merchant}:</span>
                      <span>{parsedData.company.name}</span>
                    </div>
                    {parsedData.company.tin && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t.payment.confirmation.merchant_tin}:</span>
                        <span className="font-mono text-xs">{parsedData.company.tin}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Customer email */}
                {(customerEmail || customer?.user?.email) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t.payment.confirmation.customer_email_label}:</span>
                    <span className="text-xs">{customer?.user?.email || customerEmail}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Refund notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-md mx-auto">
            <p className="text-xs text-amber-800">
              {t.payment.confirmation.refund_notice}
            </p>
          </div>

          <div className="space-y-3 max-w-md mx-auto">
            <Button variant="outline" fullWidth onClick={handleClose}>
              {t.payment.confirmation.close}
            </Button>
            <Button fullWidth onClick={handleClose}>
              {t.payment.confirmation.new_payment}
            </Button>
          </div>

          {!isLoggedIn && !postCheckoutRegistered && !postCheckoutOtpPending && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto space-y-3">
              <p className="text-sm font-medium text-blue-800">
                {t.payment.confirmation?.save_account_prompt || 'Save your card and create an account? Just enter a password:'}
              </p>
              <p className="text-xs text-blue-700">{customerEmail}</p>
              {postCheckoutError && (
                <p className="text-sm text-red-600">{postCheckoutError}</p>
              )}
              <input
                type="password"
                value={postCheckoutPassword}
                onChange={(e) => setPostCheckoutPassword(e.target.value)}
                placeholder={t.payment.confirmation?.password_placeholder || 'Password (min 8 characters)'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                value={postCheckoutPasswordConfirm}
                onChange={(e) => setPostCheckoutPasswordConfirm(e.target.value)}
                placeholder={t.payment.confirmation?.password_confirm_placeholder || 'Confirm password'}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
              <Button
                onClick={handlePostCheckoutRegistration}
                loading={postCheckoutRegistering}
                disabled={!postCheckoutPassword || !postCheckoutPasswordConfirm}
                fullWidth
              >
                {t.payment.confirmation?.create_account || 'Create account & save card'}
              </Button>
            </div>
          )}
          {postCheckoutOtpPending && !postCheckoutRegistered && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto space-y-3">
              <p className="text-sm font-medium text-blue-800">
                {t.payment.confirmation?.otp_sent || 'Verification code sent to:'}
              </p>
              <p className="text-xs text-blue-700 font-medium">{customerEmail}</p>
              {postCheckoutError && (
                <p className="text-sm text-red-600">{postCheckoutError}</p>
              )}
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={postCheckoutOtpCode}
                onChange={(e) => setPostCheckoutOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <Button
                onClick={handlePostCheckoutVerifyOtp}
                loading={postCheckoutRegistering}
                disabled={postCheckoutOtpCode.length !== 6}
                fullWidth
              >
                {t.payment.confirmation?.verify_and_save || 'Verify & save card'}
              </Button>
              <p className="text-xs text-center text-gray-500">
                {postCheckoutResendCooldown > 0 ? (
                  <span>{t.payment.confirmation?.resend_in || 'Resend in'} {postCheckoutResendCooldown}s</span>
                ) : (
                  <button type="button" onClick={handlePostCheckoutResendOtp} className="text-blue-600 hover:underline">
                    {t.payment.confirmation?.resend_code || 'Resend code'}
                  </button>
                )}
              </p>
            </div>
          )}
          {postCheckoutRegistered && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-green-800">
                {t.payment.confirmation?.account_created || 'Account created! Your card and address have been saved.'}
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <XCircle className="w-20 h-20 mx-auto text-red-500" />
          <h3 className="text-2xl font-bold text-red-600">
            {confirmationErrorCode === 'DUPLICATE_RECEIPT'
              ? t.payment.errors.receipt_already_paid
              : confirmationErrorCode === 'PAYMENT_IN_PROGRESS'
                ? t.payment.errors.payment_in_progress
                : t.payment.confirmation.failed}
          </h3>
          <p className="text-gray-600">{t.payment.confirmation.payment_failed_generic}</p>
          <Button onClick={() => { setCurrentStep('overview'); setConfirmationErrorCode(null); }} fullWidth>
            {t.payment.buttons.back}
          </Button>
        </>
      )}
    </div>
  );

  // Load Monri Components.js when monriSession is set on card step
  useEffect(() => {
    if (!monriSession || currentStep !== 'card') return;

    const scriptSrc = `${monriSession.base_url}/dist/components.js`;
    let script = document.querySelector(`script[src="${scriptSrc}"]`) as HTMLScriptElement | null;

    const initMonri = () => {
      try {
        const MonriConstructor = (window as any).Monri;
        if (!MonriConstructor) {
          setError('Failed to load Monri payment form.');
          return;
        }
        const monri = MonriConstructor(monriSession.authenticity_token, { locale: 'bs' });
        monriInstanceRef.current = monri;
        const components = monri.components({ clientSecret: monriSession.client_secret });
        const card = components.create('card', {
          style: {
            base: { fontSize: '16px', color: '#333' },
            invalid: { color: '#dc2626' },
            complete: { color: '#16a34a' },
          },
          tokenizePanOffered: true,
          locale: 'bs',
        });
        card.onChange((event: any) => {
          const errEl = document.getElementById('monri-card-errors');
          if (errEl) errEl.textContent = event.error ? event.error.message : '';
        });
        card.mount('monri-card-element');
        monriCardComponentRef.current = card;
      } catch (e) {
        console.error('Monri init error:', e);
        setError('Failed to initialize payment form.');
      }
    };

    if (script) {
      // Script already loaded
      if ((window as any).Monri) {
        initMonri();
      } else {
        script.addEventListener('load', initMonri);
      }
    } else {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.onload = initMonri;
      script.onerror = () => setError('Failed to load payment form script.');
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup: unmount card component
      if (monriCardComponentRef.current) {
        try { monriCardComponentRef.current.destroy?.(); } catch { /* ignore */ }
        monriCardComponentRef.current = null;
      }
      monriInstanceRef.current = null;
    };
  }, [monriSession?.client_secret, currentStep]);

  const handlePayWithSavedCard = async () => {
    if (!selectedSavedCardId) return;
    setLoading(true);
    setError(null);

    const token = customer?.token;
    const emailToSend = customer?.user?.email || customerEmail || undefined;
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      // Step 1: Create payment via initiation endpoint (same as handlePayment)
      let paymentId: string | null = monriSession?.payment_id || null;
      let receiptId: string | null = monriSession?.receipt_id || null;
      let amount: string | null = monriSession?.amount || null;

      if (!paymentId) {
        // Need to create the payment first
        let initRes: Response;
        if (parsedData?.fromVerification && parsedData?.receipt) {
          const payload: Record<string, unknown> = {
            tin: parsedData.receipt.tin,
            total_amount: parsedData.receipt.amount,
            pfr_number: parsedData.receipt.fiscal_number,
            pfr_time: parsedData.receipt.date_time ?? '',
            customer_email: emailToSend,
            payment_method: 'saved_card',
          };
          if (parsedData.internalReceiptId) payload.internal_receipt_id = parsedData.internalReceiptId;
          initRes = await fetch(`${API_BASE_URL}/payments/initiate-from-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
        } else if (parsedData?.qr_data) {
          initRes = await fetch(`${API_BASE_URL}/payments/initiate-from-qr`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ qr_data: parsedData.qr_data, customer_email: emailToSend, payment_method: 'saved_card' }),
          });
        } else {
          setError(t.payment.errors.payment_failed);
          setLoading(false);
          return;
        }

        const initText = await initRes.text();
        const initData = initText.trimStart().startsWith('{') ? JSON.parse(initText) : {};
        if (!initRes.ok) {
          const detail = (initData.detail ?? initData) as { message?: string; error?: string };
          setError(typeof detail === 'object' && detail?.message ? detail.message : t.payment.errors.payment_failed);
          setLoading(false);
          return;
        }
        paymentId = initData.payment_id as string;
        receiptId = initData.receipt_id as string;
        amount = initData.amount as string;
      }

      // Save new address if user entered one
      let resolvedAddressId = selectedAddressId;
      if (showNewAddressForm && isAddressValid()) {
        const newId = await saveNewAddress();
        if (newId) resolvedAddressId = newId;
      }

      // Step 2: Charge with saved card token
      const addr = getResolvedAddress();
      const savedCardBody: Record<string, unknown> = {
        payment_id: paymentId,
        card_id: selectedSavedCardId,
      };
      if (resolvedAddressId) {
        savedCardBody.address_id = resolvedAddressId;
      } else if (addr) {
        savedCardBody.full_name = addr.full_name;
        savedCardBody.address = addr.address;
        savedCardBody.city = addr.city;
        savedCardBody.zip = addr.zip;
        savedCardBody.country = addr.country;
        savedCardBody.phone = addr.phone;
      }
      const res = await fetch(`${API_BASE_URL}/payments/pay-with-saved-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(savedCardBody),
      });
      const data = await res.json();
      if (!res.ok) {
        const errMsg = data.detail || t.payment.errors.payment_failed;
        setError(typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg));
        setLoading(false);
        return;
      }

      // Transaction charged server-to-server — go to confirmation
      setPaymentResult({
        payment_id: paymentId!,
        receipt_id: receiptId!,
        amount: amount!,
        status: data.status === 'approved' ? 'completed' : 'processing',
        created_at: new Date().toISOString(),
      });
      setPaymentSuccess(true);
      setCurrentStep('confirmation');
    } catch (err) {
      console.error('Saved card payment error:', err);
      setError(t.payment.errors.payment_failed);
    } finally {
      setLoading(false);
    }
  };

  /** Post-checkout: guest registers with just a password, saves card + address + links transactions. */
  const startPostCheckoutResendCooldown = () => {
    setPostCheckoutResendCooldown(60);
    const iv = setInterval(() => {
      setPostCheckoutResendCooldown((prev) => { if (prev <= 1) { clearInterval(iv); return 0; } return prev - 1; });
    }, 1000);
  };

  /** Phase 1: register → OTP sent */
  const handlePostCheckoutRegistration = async () => {
    const email = customerEmail.trim();
    const pwd = postCheckoutPassword.trim();
    const pwdConfirm = postCheckoutPasswordConfirm.trim();
    setPostCheckoutError(null);

    if (pwd.length < 8) {
      setPostCheckoutError(t.payment.errors?.password_min || 'Password must be at least 8 characters');
      return;
    }
    if (pwd !== pwdConfirm) {
      setPostCheckoutError(t.payment.errors?.password_mismatch || 'Passwords do not match');
      return;
    }
    if (!email) {
      setPostCheckoutError(t.payment.errors?.email_required || 'Email is required');
      return;
    }

    setPostCheckoutRegistering(true);
    try {
      await unifiedAuth.registerCustomer(email, pwd);
      setPostCheckoutOtpPending(true);
      startPostCheckoutResendCooldown();
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail;
      setPostCheckoutError(detail ? String(detail) : (t.payment.errors?.register_failed || 'Registration failed'));
    } finally {
      setPostCheckoutRegistering(false);
    }
  };

  /** Phase 2: verify OTP → login → save card + address + link transactions */
  const handlePostCheckoutVerifyOtp = async () => {
    const email = customerEmail.trim();
    setPostCheckoutError(null);
    setPostCheckoutRegistering(true);
    try {
      await unifiedAuth.verifyEmail(email, postCheckoutOtpCode);
      // Login
      const { data: loginRes } = await unifiedAuth.login(email, postCheckoutPassword.trim());
      if (!loginRes.access_token) throw new Error('Login failed');
      const token = loginRes.access_token;
      setCustomerAuth(token, {
        id: loginRes.user_id as string,
        email: loginRes.email as string,
        role: loginRes.role as string,
      });

      // Save card if pan_token available
      if (lastPanTokenData) {
        try {
          await fetch(`${API_BASE_URL}/payments/save-card`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(lastPanTokenData),
          });
        } catch { /* non-critical */ }
      }

      // Save address if filled
      if (addressForm.full_name.trim() && addressForm.address.trim()) {
        try {
          await fetch(`${API_BASE_URL}/payments/addresses`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...addressForm, is_default: true }),
          });
        } catch { /* non-critical */ }
      }

      // Link guest transactions to account
      try {
        await fetch(`${API_BASE_URL}/payments/link-guest-transactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
      } catch { /* non-critical */ }

      setPostCheckoutRegistered(true);
      setPostCheckoutOtpPending(false);
    } catch (e: unknown) {
      const detail =
        e && typeof e === 'object' && 'response' in e &&
        (e.response as { data?: { detail?: string } })?.data?.detail;
      setPostCheckoutError(detail ? String(detail) : (t.payment.errors?.otp_invalid || 'Invalid verification code'));
    } finally {
      setPostCheckoutRegistering(false);
    }
  };

  const handlePostCheckoutResendOtp = async () => {
    setPostCheckoutError(null);
    try {
      await unifiedAuth.resendOtp(customerEmail.trim());
      startPostCheckoutResendCooldown();
    } catch { /* ignore */ }
  };

  const handleMonriConfirmPayment = async () => {
    if (!monriInstanceRef.current || !monriCardComponentRef.current || !monriSession) {
      setError('Payment form not ready. Please wait.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const emailToSend = (customer?.user?.email) || customerEmail || '';
      // Update payment email for guests (so backend has email for confirmation notifications)
      if (!isLoggedInAsCustomer && emailToSend && monriSession.payment_id) {
        try {
          await fetch(`${API_BASE_URL}/payments/${monriSession.payment_id}/customer-email`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ customer_email: emailToSend }),
          });
        } catch { /* non-critical */ }
      }
      const addr = getResolvedAddress();
      const result = await monriInstanceRef.current.confirmPayment(monriCardComponentRef.current, {
        address: addr?.address || '',
        fullName: addr?.full_name || emailToSend,
        city: addr?.city || '',
        zip: addr?.zip || '',
        phone: addr?.phone || '',
        country: addr?.country || 'BA',
        email: emailToSend,
        orderInfo: `Invoice payment ${monriSession.payment_id}`,
      });

      if (result.error) {
        setError(result.error.message || t.payment.errors.payment_failed);
        setLoading(false);
        return;
      }

      const paymentResult = result.result;
      if (paymentResult?.status === 'approved') {
        // Save new address if logged in user entered one
        let savedAddrId = selectedAddressId;
        if (isLoggedInAsCustomer && showNewAddressForm && isAddressValid()) {
          const newId = await saveNewAddress();
          if (newId) savedAddrId = newId;
        }
        // Save card token if Monri returned pan_token
        const panToken = paymentResult.pan_token;
        const pmData = paymentResult.payment_method?.data;
        if (isLoggedInAsCustomer && panToken && customer?.token) {
          try {
            const masked = pmData?.masked || '';
            const last4 = masked.slice(-4) || '0000';
            const cardBrand = pmData?.brand || '';
            const expDate = pmData?.expiration_date || '';
            await fetch(`${API_BASE_URL}/payments/save-card`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${customer.token}` },
              body: JSON.stringify({
                pan_token: panToken,
                last4,
                card_brand: cardBrand,
                expiration_date: expDate,
                address_id: savedAddrId || undefined,
              }),
            });
          } catch (e) {
            console.error('Failed to save card:', e);
          }
        }
        // Store pan_token for post-checkout registration (guest can register and save card after paying)
        if (panToken && pmData) {
          setLastPanTokenData({
            pan_token: panToken,
            last4: (pmData.masked || '').slice(-4) || '0000',
            card_brand: pmData.brand || '',
            expiration_date: pmData.expiration_date || '',
          });
        }
        setPaymentResult({
          payment_id: monriSession.payment_id,
          receipt_id: monriSession.receipt_id,
          monri_transaction_id: paymentResult.order_number || '',
          amount: monriSession.amount,
          status: 'completed',
          created_at: new Date().toISOString(),
        });
        setPaymentSuccess(true);
        setCurrentStep('confirmation');
      } else {
        const errMsg = paymentResult?.errors
          ? (Array.isArray(paymentResult.errors) ? paymentResult.errors.join(', ') : String(paymentResult.errors))
          : t.payment.errors.payment_failed;
        setError(errMsg);
        setPaymentSuccess(false);
        setCurrentStep('confirmation');
      }
    } catch (err) {
      console.error('Monri confirmPayment error:', err);
      setError(t.payment.errors.payment_failed);
      setPaymentSuccess(false);
      setCurrentStep('confirmation');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'scan':
        return renderScanStep();
      case 'card':
        return renderCardStep();
      case 'overview':
        return renderOverviewStep();
      case 'confirmation':
        return renderConfirmationStep();
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t.payment.modal_title}
      size="lg"
      showCloseButton={currentStep !== 'confirmation' || !paymentSuccess}
    >
      {currentStep !== 'confirmation' && renderStepIndicator()}
      {renderCurrentStep()}
    </Modal>
  );
};
